import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Translate from 'react-translate-component';
import { asyncConnect } from 'react-async-client';
import { Button, Col, Row, Spin } from 'antd';
import * as yup from 'yup';
import createDecorator from 'final-form-calculate';
import { path, omit } from 'ramda';

import { LEGAL_REQUEST_NEW } from 'constants/request';
import ErrorHTML from 'components/common/form/ErrorHTML';
import { withRouteOptions } from 'components/hocs/withRouteOptions';
import { addRequest, getRequest, getUser, getRequestSettings, getLegalOrganizations } from 'actions/asyncActions';
import ManagerForm from 'components/views/addRequest/fields/ManagerForm';
import BasicInformationForm from 'components/views/addRequest/fields/BasicInformationForm';
import WorkConditionsForm from 'components/views/addRequest/fields/WorkConditionsForm';
import RequirementsForm from 'components/views/addRequest/fields/RequirementsForm';
import LegalInfoFields from 'components/views/addRequest/fields/LegalInfoFields';
import DocumentField from 'components/views/addRequest/fields/DocumentField';
import ListenerField from 'components/common/form/ListenerField';
import withFormWrapper from 'components/hocs/withFormWrapper';
import withFormData from 'components/hocs/withFormData';
import { localStorageRequestFormKey } from 'constants/dictionaries.js';
import { isLegalFieldsEnabled, isFullLegalFieldsEnabled } from 'utils/request';

const stateToProps = state => {
    const user = getUser.selectData(state);

    return {
        user,
    };
};

const validationSchema = yup.object().shape({
    responsible: yup.number().required('responsibleNotBlank'),
    position: yup.string().required('positionNotBlank'),
    quantity: yup.number().required('enterQuantity').min(1),
    compensations: yup.string().max(1023),
    probation: yup.number(),
    requirement: yup.string().max(1023),
    responsibilities: yup.string().max(1023),
    education: yup.object().shape({
        comment: yup.string().max(1023),
    }),
    personality: yup.string().max(1023),
});

const decorators = createDecorator(
    {
        field: 'responsible',
        updates: {
            replacing: () => undefined
        },
    },
    {
        field: 'organization',
        updates: {
            department: () => undefined,
            replacing: () => undefined
        },
    }
);

@withFormData({ key: localStorageRequestFormKey })
@asyncConnect(props => ({
    getLegalOrganizations: getLegalOrganizations,
    getRequestSettings: getRequestSettings
        .withOptions({ dispatchOnMount: true, resetOnUnmount: false }),
    formAction: addRequest
        .withOptions({ autoReset: true })
        .withSuccessHandler(props => {
            props.clearFormData();
        }),
    getRequest: getRequest
        .withPayload(({ match: { params: { id }}}) => id)
        .withOptions({ dispatchOnMount: !!props.match.params.id, resetOnUnmount: true })
}), stateToProps)
@withFormWrapper({
    mapPropsToValues: props => props.match.params.id ?
        ({
            ...omit(['id'], props.getRequest.data),
            town: path(['town', 'id'], props.getRequest.data),
            townPlaceholder: path(['town', 'title'], props.getRequest.data),
            languages: path(['languages'], props.getRequest.data) || [{}],
        }) : ({
            languages: [{}],
            ...props.initialValues,
        }),
    validationSchema,
    decorators
})
@withRouteOptions
export default class AddRequestView extends Component {
    static propTypes = {
        user: PropTypes.object.isRequired,
        getLegalOrganizations: PropTypes.object.isRequired,
        getRequestSettings: PropTypes.object.isRequired,
        formAction: PropTypes.object.isRequired,
        valid: PropTypes.bool,
        submitFailed: PropTypes.bool,
        formListener: PropTypes.any,
        match: PropTypes.object
    };

    static routeOptions = {
        title: 'menu.requestAdd'
    }

    render() {
        const { valid, submitFailed, getLegalOrganizations, getRequestSettings: { meta : settingsMeta, data : settings }, formAction: { meta }, user, formListener: FormListener } = this.props;
        const invalid = submitFailed && !valid;
        const isFullLegal = isFullLegalFieldsEnabled(settings);
        const isLegal = isLegalFieldsEnabled(settings) || isFullLegal;

        return settingsMeta.pending && !settingsMeta.lastSucceedAt ? <Spin /> : (
            <Spin spinning={meta.pending}>
                <div className='card-form'>
                    { isLegal && getLegalOrganizations.meta.success && !path(['data', 'length'], getLegalOrganizations) && (
                        <Row className="card-bottom">
                            <Col xs={{ span: 24 }}>
                                <ErrorHTML className="card-error" meta={{
                                    error: { translate: 'request.legalError' }
                                }} />
                            </Col>
                        </Row>
                    )}
                    { isFullLegal ? (
                        <ListenerField
                            listenFieldName={['routeType', 'kadrsyst']}
                        >
                            { ({ routeType, kadrsyst }) => {
                                const isNewType = (routeType === LEGAL_REQUEST_NEW);
                                const hasType = isNewType || kadrsyst && kadrsyst.cell;
                                return (
                                    <Fragment>
                                        <ManagerForm {...this.props} user={user} meta={meta} />
                                        <BasicInformationForm
                                            {...this.props}
                                            meta={meta}
                                            isLegal={isLegal}
                                            isFullLegal={isFullLegal}
                                            hasType={hasType}
                                            isNewType={isNewType}
                                            legal={path(['legal', kadrsyst])}
                                        />
                                        <div style={{ display: hasType ? 'block' : 'none' }}>
                                            <WorkConditionsForm {...this.props} meta={meta} />
                                            <RequirementsForm {...this.props} meta={meta} />
                                            <DocumentField {...this.props} meta={meta} />
                                            <LegalInfoFields {...this.props} isFullLegal={isFullLegal} user={user} />
                                        </div>
                                    </Fragment>
                                );
                            }}
                        </ListenerField>
                    ) : (
                        <Fragment>
                            <ManagerForm {...this.props} user={user} meta={meta} />
                            <BasicInformationForm {...this.props} meta={meta} isLegal={isLegal} />
                            <WorkConditionsForm {...this.props} meta={meta} />
                            <RequirementsForm {...this.props} meta={meta} />
                            { isLegal && (
                                <Fragment>
                                    <DocumentField {...this.props} meta={meta} />
                                    <LegalInfoFields {...this.props} user={user} />
                                </Fragment>
                            )}
                        </Fragment>
                    )}
                    <Row className="card-bottom">
                        <Col className={cx({ invalid: invalid })} xs={{ span: 5, offset: 8 }}>
                            <Button type="primary" htmlType="submit" className="card-button" disabled={meta.pending}>
                                <Translate content='request.add' />
                            </Button>
                        </Col>
                    </Row>
                    <Row className="card-bottom">
                        <Col xs={{ span: 12, offset: 5 }}>
                            <ErrorHTML className="card-error" meta={meta} />
                        </Col>
                    </Row>
                </div>
                <FormListener />
            </Spin>
        );
    }
}
